
import Quill from 'quill'
import NrTooltip from '../NrTooltip/NrTooltip.vue'
import NrFormLabel from '../form-elements/nr-form-label.vue'
import { uniqueId } from 'lodash'

const COMPONENT_NAME = 'nr-editor'
const QUILL_FORMATS = [
  'bold',
  'indent',
  'italic',
  'link',
  'list',
  'size',
  'strike',
  'underline'
]

interface Data {
  showRequiredText: boolean
  editorContent: string
  nrEditorContainer: Element | null
  nrToolbarContainer: Element | null
  nrEditorId: string
  nrToolbarId: string
}

const __default__ = {
  components: { NrTooltip, NrFormLabel },
  emits: ['update:modelValue'],
  props: {
    required: { default: false, type: Boolean },
    resizable: { default: true, type: Boolean },
    autofocus: { default: false, type: Boolean },
    rows: { default: 5, type: Number },
    placeholder: { default: '', type: String },
    modelValue: { default: '', type: String },
    bold: { default: true, type: Boolean },
    italic: { default: true, type: Boolean },
    underline: { default: true, type: Boolean },
    strike: { default: false, type: Boolean },
    orderedList: { default: false, type: Boolean },
    bulletList: { default: false, type: Boolean },
    indent: { default: false, type: Boolean },
    outdent: { default: false, type: Boolean },
    link: { default: false, type: Boolean },
    fontSize: { default: false, type: Boolean },
    label: { type: String }
  },
  data(): Data {
    return {
      editorContent: '',
      nrEditorContainer: null,
      nrToolbarContainer: null,
      showRequiredText: false,
      nrEditorId: uniqueId(`${COMPONENT_NAME}-editor-`),
      nrToolbarId: uniqueId(`${COMPONENT_NAME}-toolbar-`)
    }
  },
  mounted() {
    this.nrEditorContainer = document.querySelector(`#${this.nrEditorId}`)
    this.nrToolbarContainer = document.querySelector(`#${this.nrToolbarId}`)

    this.initEditor()
  },
  beforeDestroy(): void {
    this.deInitEditor()
  },
  computed: {
    id() {
      return uniqueId(`${COMPONENT_NAME}-`)
    },
    editorContainerClassnames() {
      return {
        'nr-editor__content-container': true,
        'nr-editor__content-container--resizable': this.resizable
      }
    },
    editorHeight() {
      return `${Math.ceil(1.5 * this.rows)}em`
    },
    findQuill() {
      return Quill.find(this.nrEditorContainer as HTMLElement)
    }
  },
  methods: {
    async deInitEditor(): Promise<void> {
      if (this.findQuill) {
        this.findQuill.off('text-change')

        if (this.nrEditorContainer && this.nrToolbarContainer) {
          this.nrToolbarContainer.innerHTML = ''
          ;[...this.nrToolbarContainer.classList]
            .filter((classname) => /^ql-/i.test(classname))
            .forEach((classname) => {
              this.nrToolbarContainer?.classList.remove(classname)
            })

          this.nrEditorContainer.innerHTML = ''
          ;[...this.nrEditorContainer.classList]
            .filter((classname) => /^ql-/i.test(classname))
            .forEach((classname) => {
              this.nrEditorContainer?.classList.remove(classname)
            })
        }
      }
    },
    async initEditor(): Promise<void> {
      const showToolbar = !![
        this.bold,
        this.italic,
        this.underline,
        this.strike,
        this.orderedList,
        this.bulletList,
        this.indent,
        this.outdent,
        this.link,
        this.fontSize
      ].filter(Boolean).length

      if (!this.nrEditorContainer) return

      const editor = new Quill(this.nrEditorContainer as HTMLElement, {
        theme: 'snow',
        bounds: this.nrEditorContainer as HTMLElement,
        placeholder: this.placeholder,
        modules: {
          keyboard: {
            bindings: {
              tab: {
                key: 9,
                handler: function () {
                  return true
                }
              }
            }
          },
          clipboard: { matchVisual: false },
          toolbar: showToolbar ? this.nrToolbarContainer : false
        },
        formats: QUILL_FORMATS
      })

      editor.clipboard.dangerouslyPasteHTML(this.modelValue)
      this.handleOnChange()
      this.setupEditorAttributes()
      editor.on('text-change', this.handleOnChange.bind(this))
    },

    setupEditorAttributes(): void {
      const editorInputContainer = document.getElementById(
        `${this.nrEditorId}`
      ) as HTMLElement
      const editor = editorInputContainer.children[0]
      if (!editor) return
      editor.setAttribute('role', 'textbox')
      editor.setAttribute('aria-required', `${this.required}`)
      editor.setAttribute('required', `${this.required}`)
      editor.setAttribute('aria-label', this.label ? this.label : 'Editor')
      editor.setAttribute('aria-labelledby', this.label ? this.id : '')
    },

    validate(event: Event) {
      const target = event.target as HTMLInputElement
      this.showRequiredText = this.required && !target.textContent
    },

    handleOnChange(): void {
      let content = this.findQuill?.getSemanticHTML().trim()
      if (!content || content === '<p></p>') {
        content = ''
      }

      this.editorContent = content

      this.$emit('update:modelValue', this.editorContent)
    }
  },
  watch: {
    modelValue(newValue) {
      if (newValue === this.editorContent) {
        return
      }

      this.findQuill?.clipboard.dangerouslyPasteHTML(newValue)
    }
  }
}

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "e0381668": (_ctx.editorHeight)
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__